import React, { useState, useEffect } from 'react'
import {
  Edit, QrCode, Download, Phone, Mail, MapPin,
  Calendar, User, Smartphone, Package,
  FileText, History, Camera, ExternalLink, X, ChevronLeft, ChevronRight
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import Button from '../ui/Button'
import Badge from '../ui/Badge'
import Modal from '../Modal'

import { Equipamento } from '../../types'
import { getEstadoFisicoConfig } from '../../services/equipamentos'
import equipamentosService from '../../services/equipamentos'

interface EquipamentoDetailsProps {
  equipamento: Equipamento
  onEdit: () => void
  onClose: () => void
}

const EquipamentoDetails: React.FC<EquipamentoDetailsProps> = ({
  equipamento,
  onEdit,
  onClose
}) => {
  const [showQrCode, setShowQrCode] = useState(false)
  const [qrCodeData, setQrCodeData] = useState<{ qrCode: string; qrCodeImage: string } | null>(null)
  const [fotoSelecionada, setFotoSelecionada] = useState<string | null>(null)
  const [indiceFotoAtual, setIndiceFotoAtual] = useState<number>(0)

  // Função para construir URL da imagem (relativa para aproveitar proxy do Vite)
  const buildImageUrl = (url: string): string => {
    if (url.startsWith('http')) return url
    // Usar URL relativa que será proxy pelo Vite
    return url.startsWith('/uploads') ? url : `/uploads${url}`
  }

  // Processar fotos de forma segura
  const processarFotos = (): string[] => {
    if (!equipamento?.fotos) return []

    try {
      // Se fotos é uma string JSON, fazer parse
      if (typeof equipamento.fotos === 'string') {
        const fotosArray = JSON.parse(equipamento.fotos)
        return Array.isArray(fotosArray) ? fotosArray.map(buildImageUrl) : []
      }
      // Se já é array
      if (Array.isArray(equipamento.fotos)) {
        return equipamento.fotos.map(buildImageUrl)
      }
    } catch (error) {
      console.error('Erro ao processar fotos do equipamento:', error)
    }

    return []
  }

  const fotos = processarFotos()

  // Funções de navegação de fotos
  const handleOpenPhoto = (foto: string, index: number) => {
    setFotoSelecionada(foto)
    setIndiceFotoAtual(index)
  }

  const handleClosePhoto = () => {
    setFotoSelecionada(null)
    setIndiceFotoAtual(0)
  }

  const handlePreviousPhoto = () => {
    if (indiceFotoAtual > 0) {
      const novoIndice = indiceFotoAtual - 1
      setIndiceFotoAtual(novoIndice)
      setFotoSelecionada(fotos[novoIndice])
    }
  }

  const handleNextPhoto = () => {
    if (indiceFotoAtual < fotos.length - 1) {
      const novoIndice = indiceFotoAtual + 1
      setIndiceFotoAtual(novoIndice)
      setFotoSelecionada(fotos[novoIndice])
    }
  }

  // Navegação por teclado
  const handleKeyDown = (e: KeyboardEvent) => {
    if (!fotoSelecionada) return
    switch (e.key) {
      case 'Escape':
        handleClosePhoto()
        break
      case 'ArrowLeft':
        handlePreviousPhoto()
        break
      case 'ArrowRight':
        handleNextPhoto()
        break
    }
  }

  // useEffect para keyboard navigation
  useEffect(() => {
    if (fotoSelecionada) {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [fotoSelecionada])

  // Query para buscar dados completos do equipamento
  const { data: equipamentoCompleto } = useQuery({
    queryKey: ['equipamento', equipamento.id],
    queryFn: () => equipamentosService.getById(equipamento.id),
    initialData: equipamento
  })

  const estadoConfig = getEstadoFisicoConfig(equipamento.estadoFisico)

  const handleShowQrCode = async () => {
    try {
      if (equipamento.qrCode) {
        // Se já tem QR Code, gerar a imagem
        const data = await equipamentosService.generateQrCode(equipamento.id)
        setQrCodeData(data)
        setShowQrCode(true)
      }
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error)
    }
  }

  const handleDownloadQrCode = () => {
    if (qrCodeData?.qrCodeImage) {
      const link = document.createElement('a')
      link.download = `qr-code-equipamento-${equipamento.id}.png`
      link.href = qrCodeData.qrCodeImage
      link.click()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header com ações */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <Smartphone className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {equipamento.modelo.marca.nome} {equipamento.modelo.nome}
            </h2>
            <p className="text-gray-600">{equipamento.modelo.tipoEquipamento.nome}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {equipamento.qrCode && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleShowQrCode}
              leftIcon={<QrCode className="h-4 w-4" />}
            >
              QR Code
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            leftIcon={<Edit className="h-4 w-4" />}
          >
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações principais */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dados do equipamento */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Informações do Equipamento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Tipo</label>
                <p className="text-sm text-gray-900">{equipamento.modelo.tipoEquipamento.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Marca</label>
                <p className="text-sm text-gray-900">{equipamento.modelo.marca.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Modelo</label>
                <p className="text-sm text-gray-900">{equipamento.modelo.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Estado Físico</label>
                <Badge
                  variant={estadoConfig.color as any}
                  text={estadoConfig.label}
                />
              </div>
              {equipamento.numeroSerie && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Número de Série</label>
                  <p className="text-sm text-gray-900 font-mono">{equipamento.numeroSerie}</p>
                </div>
              )}
              {equipamento.imei && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">IMEI</label>
                  <p className="text-sm text-gray-900 font-mono">{equipamento.imei}</p>
                </div>
              )}
              {equipamento.cor && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Cor</label>
                  <p className="text-sm text-gray-900">{equipamento.cor}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-500">Data de Registo</label>
                <p className="text-sm text-gray-900">
                  {new Date(equipamento.createdAt).toLocaleDateString('pt-PT', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Dados do cliente */}
          {equipamento.cliente && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5" />
                Informações do Cliente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Nome</label>
                  <p className="text-sm text-gray-900">{equipamento.cliente.nome}</p>
                </div>
                {equipamento.cliente.telefonePrincipal && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Telefone</label>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-900">{equipamento.cliente.telefonePrincipal}</p>
                    </div>
                  </div>
                )}
                {equipamento.cliente.email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-900">{equipamento.cliente.email}</p>
                    </div>
                  </div>
                )}
                {equipamento.cliente.morada && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500">Morada</label>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div className="text-sm text-gray-900">
                        <p>{equipamento.cliente.morada}</p>
                        {equipamento.cliente.codigoPostal && equipamento.cliente.cidade && (
                          <p>{equipamento.cliente.codigoPostal} {equipamento.cliente.cidade}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Observações */}
          {equipamento.observacoes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Observações
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {equipamento.observacoes}
              </p>
            </div>
          )}

          {/* Fotos */}
          {fotos.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Fotos do Equipamento
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {fotos.map((foto, index) => (
                  <div key={index} className="group relative">
                    <img
                      src={foto}
                      alt={`Foto ${index + 1} do equipamento`}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleOpenPhoto(foto, index)}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center pointer-events-none">
                      <ExternalLink className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar com estatísticas e ações rápidas */}
        <div className="space-y-6">
          {/* Estatísticas rápidas */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Estatísticas</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Ordens de Serviço</span>
                <span className="text-sm font-medium text-gray-900">
                  {equipamentoCompleto?._count?.ordensServico || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Data de Registo</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(equipamento.createdAt).toLocaleDateString('pt-PT')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Última Atualização</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(equipamento.updatedAt).toLocaleDateString('pt-PT')}
                </span>
              </div>
            </div>
          </div>

          {/* Ações rápidas */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Ações Rápidas</h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<FileText className="h-4 w-4" />}
                onClick={() => {
                  // Navegar para nova ordem de serviço
                  console.log('Nova OS para equipamento:', equipamento.id)
                }}
              >
                Nova Ordem de Serviço
              </Button>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<History className="h-4 w-4" />}
                onClick={() => {
                  // Ver histórico
                  console.log('Ver histórico do equipamento:', equipamento.id)
                }}
              >
                Ver Histórico
              </Button>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<Download className="h-4 w-4" />}
                onClick={() => {
                  // Exportar dados
                  console.log('Exportar dados do equipamento:', equipamento.id)
                }}
              >
                Exportar Dados
              </Button>
            </div>
          </div>

          {/* QR Code info */}
          {equipamento.qrCode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <QrCode className="h-5 w-5 text-blue-600" />
                <h3 className="text-sm font-medium text-blue-900">QR Code</h3>
              </div>
              <p className="text-sm text-blue-700 mb-3">
                Este equipamento possui um código QR único para identificação rápida.
              </p>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={handleShowQrCode}
              >
                Visualizar QR Code
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Modal QR Code */}
      <Modal
        isOpen={showQrCode}
        onClose={() => setShowQrCode(false)}
        title="QR Code do Equipamento"
        size="sm"
      >
        <div className="text-center space-y-4">
          {qrCodeData?.qrCodeImage && (
            <>
              <div className="flex justify-center">
                <img
                  src={qrCodeData.qrCodeImage}
                  alt="QR Code do Equipamento"
                  className="w-64 h-64 border border-gray-200 rounded-lg"
                />
              </div>
              <p className="text-sm text-gray-600">
                Use este QR Code para identificar rapidamente o equipamento
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setShowQrCode(false)}
                >
                  Fechar
                </Button>
                <Button
                  onClick={handleDownloadQrCode}
                  leftIcon={<Download className="h-4 w-4" />}
                >
                  Download
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Modal para visualizar foto */}
      {fotoSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]" onClick={handleClosePhoto}>
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            {/* Botão de fechar */}
            <button
              onClick={handleClosePhoto}
              className="absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>

            {/* Contador de fotos */}
            {fotos.length > 1 && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm z-10">
                {indiceFotoAtual + 1} de {fotos.length}
              </div>
            )}

            {/* Botão de navegação anterior */}
            {fotos.length > 1 && indiceFotoAtual > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handlePreviousPhoto()
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all z-10"
              >
                <ChevronLeft className="h-6 w-6 text-gray-800" />
              </button>
            )}

            {/* Botão de navegação próxima */}
            {fotos.length > 1 && indiceFotoAtual < fotos.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleNextPhoto()
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all z-10"
              >
                <ChevronRight className="h-6 w-6 text-gray-800" />
              </button>
            )}

            <img
              src={fotoSelecionada}
              alt="Foto ampliada"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default EquipamentoDetails